import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggle, update } from '../../Actions/Cart.action';
import { translate } from '../../Services/translation';
import BftRemoveFromCartButton from './BftRemoveFromCartButton';

const CartDrawer = () => {
  if (!(window.__litium?.bft?.purchaseEnabled ?? true)) {
    return null;
  }

  const dispatch = useDispatch();
  const {
    orderRows,
    orderTotal,
    orderLevelDiscountRows = [],
    quantity,
    showInfo,
    checkoutUrl,
  } = useSelector((state) => state.cart);

  const onClose = () => dispatch(toggle());

  useEffect(() => {
    if (showInfo) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = scrollbarWidth + 'px';
      }
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showInfo]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showInfo) {
        dispatch(toggle());
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showInfo, dispatch]);

  const handleQuantityChange = (articleNumber, currentQuantity, delta) => {
    const newQuantity = currentQuantity + delta;
    if (newQuantity < 0) return;
    dispatch(update(articleNumber, newQuantity));
  };

  const handleRemove = (articleNumber) => {
    return dispatch(update(articleNumber, 0));
  };

  return (
    <div className="cart cart--mini">
      <a
        className="cart__link--block"
        rel="nofollow"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          onClose();
        }}
      >
        <i className="cart__icon">
          <span className="cart__quantity">{quantity}</span>
        </i>
        <span className="cart__title hide">
          {translate('minicart.checkout')}
        </span>
      </a>

      {showInfo && (
        <div
          className="cart-drawer__overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div
        className={`cart-drawer__panel${
          showInfo ? ' cart-drawer__panel--open' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={translate('bft.minicarttocheckout')}
      >
        <div className="cart-drawer__header">
          <h2 className="cart-drawer__title">
            {translate('bft.minicartheader')}
          </h2>
          <button
            className="cart-drawer__close"
            onClick={onClose}
            aria-label={translate('bft.minicartclose')}
          >
            &times;
          </button>
        </div>

        <div className="cart-drawer__items">
          {!orderRows || orderRows.length === 0 ? (
            <p className="cart-drawer__empty">
              {translate('bft.minicartempty')}
            </p>
          ) : (
            orderRows.map((order) => (
              <div key={order.rowSystemId} className="cart-drawer__item">
                <a
                  href={order.url}
                  className="cart-drawer__item-image-link"
                  tabIndex="-1"
                >
                  <img
                    className="cart-drawer__item-image"
                    src={order.image}
                    alt={order.name}
                  />
                </a>
                <div className="cart-drawer__item-details">
                  <a href={order.url} className="cart-drawer__item-name">
                    {order.name}
                  </a>
                  <div className="cart-drawer__item-price">
                    {order.campaignPrice ? (
                      <>
                        <span className="cart-drawer__item-price--campaign">
                          {order.totalCampaignPrice}
                        </span>
                        <span className="cart-drawer__item-price--original">
                          {order.totalPrice}
                        </span>
                      </>
                    ) : (
                      <span>{order.totalPrice}</span>
                    )}
                  </div>
                  {!order.isFreeGift && (
                    <div className="cart-drawer__item-controls">
                      <button
                        className="cart-drawer__qty-btn"
                        onClick={() =>
                          handleQuantityChange(
                            order.articleNumber,
                            order.quantity,
                            -1
                          )
                        }
                        aria-label="Minska antal"
                      >
                        &minus;
                      </button>
                      <span className="cart-drawer__qty">
                        {order.quantityString}
                      </span>
                      <button
                        className="cart-drawer__qty-btn"
                        onClick={() =>
                          handleQuantityChange(
                            order.articleNumber,
                            order.quantity,
                            1
                          )
                        }
                        aria-label="Öka antal"
                      >
                        &#43;
                      </button>
                      <BftRemoveFromCartButton
                        onClick={() => handleRemove(order.articleNumber)}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="cart-drawer__footer">
          {orderLevelDiscountRows.length > 0 && (
            <div className="cart-drawer__discounts">
              {orderLevelDiscountRows.map((discountRow, index) => (
                <div
                  key={`${discountRow.rowSystemId}-order-level-${index}`}
                  className="cart-drawer__discount-row"
                >
                  <span className="cart-drawer__discount-name">{discountRow.name}</span>
                  <span className="cart-drawer__discount-amount">{discountRow.totalPrice}</span>
                </div>
              ))}
            </div>
          )}
          <div className="cart-drawer__total">
            <span>{translate('minicart.total')}</span>
            <span className="cart-drawer__total-amount">{orderTotal}</span>
          </div>
          <a
            href={checkoutUrl}
            className="button expanded cart-drawer__checkout-btn"
          >
            {translate('bft.minicarttocheckout')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
